import React from 'react';
import { mathFormat, formatPercentValue } from '@sg/utils/common';
// import { history } from 'umi';
// import moment from 'moment';
// import ContentCard from '@/pages/strategic/components/ContentCard';
// import TableFormSummary from '@sg/components/TableFormSummary';
// import { mapBoardGovernanceStatus, ROUTE_PORTFOLIO_OVERVIEW_BOARD_GOVERNANCE } from '../config';
// import { createBtnPoint, SGBuriedBtnPoint, StudiosTab } from '@/pages/strategic/utils/buriedPoint';

// Chart default configurations
export const DefaultFinancialsChat = {
  icon: "discount-chart",
  value: "bar",
  tip: "bar",
  format: false,
};

// Chart dimensions and display options — single source of truth for studio card layout
export const STUDIO_CARD_CHART_HEIGHT = 343;
export const height = `${STUDIO_CARD_CHART_HEIGHT}px`;

/** Margin above table in CSS (`.dbs-chats-table`); not subtracted from body — chart has no equivalent gap */
export const STUDIO_CARD_TABLE_TOP_MARGIN = 20;
export const STUDIO_CARD_TABLE_HEADER_ROW_HEIGHT = 50;
/** Headcount category radio row (margin-top 16px + radio) */
export const STUDIO_CARD_HEADCOUNT_RADIO_OFFSET = 29;

export interface IStudioCardTableMaxHeightOptions {
  hasHeadcountCategoryRadio?: boolean;
  tableHeaderRows?: 1 | 2;
}

export const getStudioCardChartHeight = (
  options: Pick<IStudioCardTableMaxHeightOptions, 'hasHeadcountCategoryRadio'> = {},
): string => {
  // Headcount shows a category radio above chart/table; shrink content so card bottom aligns with Financials.
  const offset = options.hasHeadcountCategoryRadio
    ? STUDIO_CARD_HEADCOUNT_RADIO_OFFSET
    : 0;
  return `${STUDIO_CARD_CHART_HEIGHT - offset}px`;
};

/** scrollable maxHeight — tea-table (header + body) should match chart height */
export const getStudioCardTableBodyMaxHeight = (
  options: IStudioCardTableMaxHeightOptions = {},
): string => {
  const radioOffset = options.hasHeadcountCategoryRadio
    ? STUDIO_CARD_HEADCOUNT_RADIO_OFFSET
    : 0;
  // Same content height as the chart beside it (e.g. 343 - 29 = 314 for Headcount).
  const chartHeight = STUDIO_CARD_CHART_HEIGHT - radioOffset;
  const headerRows = options.tableHeaderRows ?? 1;
  // scrollable maxHeight applies to tbody only; subtract rendered <th> rows so
  // header + body ≈ chartHeight (th ≈ 50.67px with padding/borders, not 50).
  const bodyHeight = chartHeight - headerRows * STUDIO_CARD_TABLE_HEADER_ROW_HEIGHT;

  // Round to 2 decimals to avoid sub-pixel overflow on tea-table--allbordered.
  return `${Math.max(Math.round(bodyHeight * 100) / 100, 120)}px`;
};

export const showFinancialsList = ['bar', 'table'];

// Segment options for date type
export const getOptionsSegment = (formatMessage: any) => [
  { text: formatMessage({ id: 'dbs_studios_by_month' }), value: "month" },
  { text: formatMessage({ id: 'dbs_studios_by_year' }), value: "year" },
];

// Tabs for headcount section
export const getTabs = (formatMessage: any) => [
  { id: "headcount", label: formatMessage({ id: 'dbs_studios_headcount' }) },
  { id: "staffrate", label: `${formatMessage({ id: 'dbs_studios_staff_rate' })} ($/FTE)` },
];

export const getFinancialTabs = (formatMessage: any) => [
  { id: 'financials', label: `${formatMessage({ id: 'dbs_financials' })} ($)` },
  { id: 'burnrate', label: `${formatMessage({ id: 'dbs_burn_rate' })} ($)` },
];

// Table column configurations
export const FinancialsColumns = [
  {
    header: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 60,
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>Revenue</div>,
    title: 'Revenue',
    dataIndex: 'Revenue',
    key: 'Revenue',
    format: mathFormat,
    width: 100,
    render: (item: any) => <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.Revenue)}</div>
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>Profit</div>,
    title: 'Profit',
    dataIndex: 'Profit',
    key: 'Profit',
    width: 100,
    format: mathFormat,
    render: (item: any) => <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.Profit)}</div>
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap', paddingRight: '20px' }}>Profitability (%)</div>,
    title: 'Profitability (%)',
    dataIndex: 'Profitability',
    key: 'Profitability',
    width: 120,
    format: formatPercentValue,
    render: (item: any) => <div style={{ textAlign: 'right', textWrap: 'nowrap', paddingRight: '20px' }}>{formatPercentValue(item?.Profitability)}</div>
  },
];
